import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable, of as observableOf, Subject, throwError as observableThrowError } from 'rxjs';

import { FeatureFlags, FeatureFlagVariants } from '@app/core/feature-flags/feature-flags';
import { LaunchDarklyService } from '@app/core/feature-flags/launchdarkly.service';
import { MODULE_ACCOUNT_SETUP_PAGE } from '@app/core/mixpanel.constants';
import { StepName } from '@app/registration/enterprise/registration-step-name';
import { Address } from '@app/shared/address';

import { EnterpriseRegistration } from '../enterprise-registration';
import { EnterpriseRegistrationAnalyticsService } from '../enterprise-registration-analytics.service';
import { MembershipType } from '../membership-type';
import { RegistrationStep } from '../registration-step';
import { AccountSetUpStepComponent } from './account-set-up-step.component';

@Injectable()
export class AccountSetUpConfig extends RegistrationStep {
  GA_LABEL = 'Account_Setup_Step';

  MODULE = MODULE_ACCOUNT_SETUP_PAGE;
  component = AccountSetUpStepComponent;
  progress = 30;
  showLoginLink = true;
  shouldDisableNameFields = false;

  form: UntypedFormGroup = this.formBuilder.group({
    address: this.formBuilder.group(Address.buildBasicAddressControlsConfig()),
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    phoneNumber: '',
    preferredName: '',
    textAppToPhone: true,
  });

  private destroy$ = new Subject<void>();

  constructor(
    private enterpriseRegistrationAnalyticsService: EnterpriseRegistrationAnalyticsService,
    private formBuilder: UntypedFormBuilder,
    private launchDarklyService: LaunchDarklyService,
  ) {
    super();
  }

  private configureFieldsBasedOnMembershipType(membershipType: string, shouldDisableNameFields: boolean) {
    if (membershipType === MembershipType.PERSONAL && shouldDisableNameFields) {
      this.form.controls.firstName.disable();
      this.form.controls.lastName.disable();
    } else {
      this.form.controls.firstName.enable();
      this.form.controls.lastName.enable();
    }
  }

  initComponent(component: AccountSetUpStepComponent, state: EnterpriseRegistration) {
    component.form = this.form;
    component.showValueProp = state.prefillFromEloqua;
    this.trackPageView();
    component.isRpcEligible = state.isRpcEligible;
    const membershipType = state.details?.membershipType;
    this.launchDarklyService
      .featureFlag$(FeatureFlags.REMOTE_PRIMARY_CARE_ENABLED, FeatureFlagVariants.OFF)
      .subscribe(flag => {
        const isEnabled = flag !== FeatureFlagVariants.OFF;
        if (isEnabled) {
          if (component.isRpcEligible && membershipType) {
            this.configureFieldsBasedOnMembershipType(membershipType, this.shouldDisableNameFields);
          }
        }
      });
  }

  onDestroy() {
    this.destroy$.next();
    this.form.controls.phoneNumber.clearValidators();
  }

  patchParams(params: Record<string, any>) {
    const { firstName, lastName, address, phoneNumber, preferredName, shouldDisableNameFields } = params;
    if (firstName) {
      this.form.patchValue({ firstName });
    }
    if (lastName) {
      this.form.patchValue({ lastName });
    }
    if (address) {
      this.form.patchValue({ address });
    }
    if (phoneNumber) {
      this.form.patchValue({ phoneNumber });
    }
    if (preferredName) {
      this.form.patchValue({ preferredName });
    }
    if (shouldDisableNameFields !== undefined) {
      this.shouldDisableNameFields = shouldDisableNameFields;
    }
  }

  submit(state: EnterpriseRegistration): Observable<any> {
    if (this.form.controls.address.invalid && !state.fastForwarding) {
      this.enterpriseRegistrationAnalyticsService.invalidAddressSelected('', state.isWhitelisted);
    }

    if (state.isRpcEligible) {
      // Address.markInvalidFieldsAsDirty(this.form.get('address') as UntypedFormGroup);
    }

    if (this.form.valid) {
      state.patient.address = this.form.controls.address.getRawValue();
      state.patient.firstName = this.form.controls.firstName.value;
      state.patient.lastName = this.form.controls.lastName.value;
      state.patient.preferredName = this.form.controls.preferredName.value;
      state.patient.phoneNumber = this.form.controls.phoneNumber.value;
      state.setCurrentStep(StepName.dobSex);
      this.trackSubmission(state);
      return observableOf(true);
    }
    return observableThrowError(new Error());
  }

  private trackPageView() {
    this.enterpriseRegistrationAnalyticsService.trackWithLaunchDarkly({
      key: 'Enterprise Registration Account Set Up Page Viewed',
    });
  }

  private trackSubmission(state: EnterpriseRegistration) {
    this.enterpriseRegistrationAnalyticsService.regInputSubmitted({
      module: this.MODULE,
      isWhitelist: state.isWhitelisted,
      fastForwarding: state.fastForwarding,
      relationshipType: state.details.membershipType,
    });
  }
}
